import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { contentAPI } from '../services/api';
import { useToast } from './Toast';
import './DraftRevisionsModal.css';

function normalizeOutline(raw) {
  if (raw == null) return [];
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (Array.isArray(data)) return data;
  if (data.main_sections && Array.isArray(data.main_sections)) {
    return data.main_sections;
  }
  return [];
}

function outlineToEditableMarkdown(sections) {
  const rows = [];
  for (const sec of normalizeOutline(sections)) {
    const h = (sec.heading || '').trim();
    if (h) rows.push(`## ${h}`, '');
    for (const kp of (sec.key_points || [])
      .map((k) => String(k).trim())
      .filter(Boolean)) {
      rows.push(`- ${kp}`);
    }
    const subs = sec.subsections || [];
    if (subs.length) {
      if (rows.length && rows[rows.length - 1] !== '') rows.push('');
      for (const sub of subs) {
        const sh = (sub.heading || '').trim();
        if (sh) rows.push(`### ${sh}`, '');
        for (const kp of (sub.key_points || [])
          .map((k) => String(k).trim())
          .filter(Boolean)) {
          rows.push(`- ${kp}`);
        }
        rows.push('');
      }
    }
    rows.push('');
  }
  return rows.join('\n').trim();
}

function formatSnapshotLabel(snap) {
  if (snap.description && snap.description.trim()) {
    return snap.description.trim();
  }
  if (!snap.created_at) return `Snapshot #${snap.id}`;
  const d = new Date(snap.created_at);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relativeTime(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function kindBadge(kind) {
  if (kind === 'pre_restore') return 'Before restore';
  return 'Manual';
}

function PreviewSection({ title, text, onCopy }) {
  const value = text || '';
  return (
    <div className="drm-preview-section">
      <div className="drm-preview-section-head">
        <h5>{title}</h5>
        <button
          type="button"
          className="btn-secondary drm-copy-btn"
          disabled={!value}
          onClick={() => onCopy(value, title)}
        >
          Copy
        </button>
      </div>
      <textarea
        className="drm-preview-text"
        readOnly
        value={value}
        rows={Math.min(16, Math.max(4, value.split('\n').length + 1))}
      />
    </div>
  );
}

export default function DraftRevisionsModal({
  draftId,
  open,
  onClose,
  onRestored,
}) {
  const { showToast } = useToast();
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [activeTab, setActiveTab] = useState('article');

  const loadList = useCallback(async ({ selectFirst = false } = {}) => {
    try {
      setLoadingList(true);
      const res = await contentAPI.listDraftSnapshots(draftId);
      const snaps = res.data.snapshots || [];
      setList(snaps);
      if (snaps.length && selectFirst) {
        setSelectedId(snaps[0].id);
      }
    } catch (err) {
      showToast(
        `Failed to load revisions: ${err.response?.data?.error || err.message}`,
        'error'
      );
    } finally {
      setLoadingList(false);
    }
  }, [draftId, showToast]);

  useEffect(() => {
    if (!open || !draftId) return;
    setSelectedId(null);
    setDetail(null);
    void loadList({ selectFirst: true });
  }, [open, draftId, loadList]);

  useEffect(() => {
    if (!open || !draftId || !selectedId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingDetail(true);
        const res = await contentAPI.getDraftSnapshot(draftId, selectedId);
        if (!cancelled) {
          setDetail(res.data);
          setActiveTab('article');
        }
      } catch (err) {
        if (!cancelled) {
          showToast(
            `Failed to load snapshot: ${err.response?.data?.error || err.message}`,
            'error'
          );
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, draftId, selectedId, showToast]);

  const payload = detail?.payload || {};
  const articleText = payload.content || '';
  const summaryText = payload.executive_summary || '';
  const outlineText = outlineToEditableMarkdown(payload.outline);
  const researchText = detail?.include_research_brief
    ? JSON.stringify(payload.research_brief || {}, null, 2)
    : '';

  const tabs = useMemo(() => {
    const t = [
      { id: 'article', label: 'Article' },
      { id: 'summary', label: 'Summary' },
      { id: 'outline', label: 'Outline' },
    ];
    if (detail?.include_research_brief) {
      t.push({ id: 'research', label: 'Research' });
    }
    return t;
  }, [detail?.include_research_brief]);

  const handleCopy = async (text, sectionName) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${sectionName} copied to clipboard.`, 'success');
    } catch {
      showToast('Could not copy to clipboard.', 'error');
    }
  };

  const handleRestore = async () => {
    if (!selectedId) return;
    const label = formatSnapshotLabel(
      list.find((s) => s.id === selectedId) || { id: selectedId }
    );
    if (
      !window.confirm(
        `Restore "${label}"?\n\nYour current draft will be saved as a "Before restore" snapshot first.`
      )
    ) {
      return;
    }
    try {
      setRestoring(true);
      const res = await contentAPI.restoreDraftSnapshot(draftId, selectedId);
      showToast(
        'Draft restored. A backup snapshot was saved in Revisions.',
        'success'
      );
      if (onRestored) await onRestored(res.data);
      await loadList();
      if (res.data.backup_snapshot_id) {
        setSelectedId(res.data.backup_snapshot_id);
      }
    } catch (err) {
      showToast(
        `Restore failed: ${err.response?.data?.error || err.message}`,
        'error'
      );
    } finally {
      setRestoring(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="drm-backdrop"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="drm-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drm-head">
          <h3 id="drm-title">Revisions</h3>
          <button type="button" className="drm-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="drm-body">
          <aside className="drm-list">
            {loadingList && <p className="drm-muted">Loading…</p>}
            {!loadingList && list.length === 0 && (
              <p className="drm-muted">No snapshots yet. Use Save snapshot to create one.</p>
            )}
            <ul className="drm-list-ul">
              {list.map((snap) => (
                <li key={snap.id}>
                  <button
                    type="button"
                    className={`drm-list-item ${selectedId === snap.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(snap.id)}
                  >
                    <span className="drm-list-title">{formatSnapshotLabel(snap)}</span>
                    <span className="drm-list-meta">
                      {relativeTime(snap.created_at)}
                      <span className={`drm-badge drm-badge--${snap.kind}`}>
                        {kindBadge(snap.kind)}
                      </span>
                      {snap.include_research_brief && (
                        <span className="drm-badge drm-badge--research">Research</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="drm-preview">
            {!selectedId && (
              <p className="drm-muted">Select a revision to preview.</p>
            )}
            {selectedId && loadingDetail && (
              <p className="drm-muted">Loading snapshot…</p>
            )}
            {selectedId && !loadingDetail && detail && (
              <>
                <nav className="drm-tabs" aria-label="Snapshot sections">
                  {tabs.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`drm-tab ${activeTab === t.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(t.id)}
                    >
                      {t.label}
                    </button>
                  ))}
                </nav>
                {activeTab === 'article' && (
                  <PreviewSection
                    title="Article"
                    text={articleText}
                    onCopy={handleCopy}
                  />
                )}
                {activeTab === 'summary' && (
                  <PreviewSection
                    title="Summary"
                    text={summaryText}
                    onCopy={handleCopy}
                  />
                )}
                {activeTab === 'outline' && (
                  <PreviewSection
                    title="Outline"
                    text={outlineText}
                    onCopy={handleCopy}
                  />
                )}
                {activeTab === 'research' && detail.include_research_brief && (
                  <PreviewSection
                    title="Research packet"
                    text={researchText}
                    onCopy={handleCopy}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="drm-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn-primary drm-restore-btn"
            disabled={!selectedId || restoring || loadingDetail}
            onClick={() => void handleRestore()}
          >
            {restoring ? 'Restoring…' : 'Restore this version'}
          </button>
        </div>
      </div>
    </div>
  );
}
